import { useState } from "react";
import { Breadcrumb, Button, TextInput } from "flowbite-react";
import { HiHome, HiPlus, HiSearch } from "react-icons/hi";
import NavbarSidebarLayout from "@/layouts/navbar-sidebar";
import { useTranslation } from "react-i18next";
import { useGetCitiesQuery, useCreateCityMutation } from "@/api/locationApi/city";
import { getErrorMessage } from "@/utils/requestUtils";
import { toast } from "@/lib/toast";
import CityListTable from "./CityListTable";
import CityFormDialog from "./CityFormDialog";
import { City, QueryParams } from "@/sharedTypes";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isCompanyAdminUser } from "@/constants/agent";

export default function CityList() {
  const { t } = useTranslation(["temporalActivity", "common"]);
  const user = useCurrentUser();

  const [queryParams, setQueryParams] = useState<QueryParams>({
    filters: {},
    pagination: { page: 1, pageSize: 15 },
  });
  const [search, setSearch] = useState("");

  const { data: citiesData } = useGetCitiesQuery(queryParams);
  const [createCity] = useCreateCityMutation();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const handleCreate = (data: City) => {
    createCity(data)
      .unwrap()
      .then(() => {
        toast.success(t("temporalActivity:city.createdSuccess"));
        setIsCreateModalOpen(false);
      })
      .catch((err) => toast.error(getErrorMessage(err)));
  };

  const handlePageChange = (page: number) => {
    setQueryParams((prev) => ({ ...prev, pagination: { ...prev.pagination, page } }));
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setQueryParams((prev) => ({
      ...prev,
      filters: value ? { "city_name[like]": value } : {},
      pagination: { ...prev.pagination, page: 1 },
    }));
  };

  return (
    <NavbarSidebarLayout>
      <div className="h-screen overflow-y-auto pb-20">
        <div className="mb-6 grid grid-cols-1 gap-y-6 px-4 pt-6 dark:border-gray-700 dark:bg-gray-900">
          <div className="col-span-full">
            <Breadcrumb className="mb-4">
              <Breadcrumb.Item href="/">
                <div className="flex items-center gap-x-3">
                  <HiHome className="text-xl" />
                  <span className="dark:text-white">{t("common:home")}</span>
                </div>
              </Breadcrumb.Item>
              <Breadcrumb.Item>{t("temporalActivity:city.title")}</Breadcrumb.Item>
            </Breadcrumb>

            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
                  {t("temporalActivity:city.title")}
                </h1>
                <p className="text-gray-500">{t("temporalActivity:city.manage")}</p>
              </div>
              {isCompanyAdminUser(user) && (
                <Button color="primary" onClick={() => setIsCreateModalOpen(true)}>
                  <HiPlus className="mr-2 h-5 w-5" />
                  {t("temporalActivity:city.create")}
                </Button>
              )}
            </div>

            <div className="mt-4 max-w-xs">
              <TextInput
                icon={HiSearch}
                placeholder={t("temporalActivity:city.searchPlaceholder")}
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
            </div>
          </div>

          <CityListTable
            data={citiesData?.items || []}
            pagination={
              citiesData?.pagination
                ? {
                    pageSize: citiesData.pagination.pageSize || 15,
                    pageIndex: citiesData.pagination.pageIndex ?? citiesData.pagination.page ?? 1,
                    totalPages: citiesData.pagination.totalPages ?? 1,
                    totalItems: citiesData.pagination.totalItems ?? citiesData.pagination.total ?? 0,
                  }
                : { pageSize: 15, pageIndex: 1, totalPages: 1, totalItems: 0 }
            }
            onPageChange={handlePageChange}
          />
        </div>
      </div>

      <CityFormDialog open={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} onSubmit={handleCreate} />
    </NavbarSidebarLayout>
  );
}
